import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import { Table } from "antd";
import { withTranslation } from "react-i18next";
import "./cursor.less";
import styled from "styled-components";

import { selectRecentPatients } from "@tailormed/common-client/store/reselectSelector";
import { selectTableColumns, selectIsFetching } from "../../store/selector";
import { ApiNames } from "../../api/api";
import ACTION from "../../store/action";
import { PAGE_SIZE, selectedViews } from "../../constant/table";
import { TailormedSpinner } from "../TailormedSpinner/TailormedSpinner";
import { EditColumn, PatientNameColumn, StarColumn, ViewedAtColumn } from "./CommonColumns";
import PatientPageCount from "./PatientPageCount";
import { TabsMap } from "../../screen/patient/Patient";
import { commonTableStyle } from "./TableStyle";
import EmptyTableMessage from "./EmptyTableMessage";
import { fetchTableData } from "../../utils/fetchTableData";
import { useHistory } from "react-router-dom";
import LinkableCell from "../UI/LinkableCell";
import { handleRowNavigation } from "../../utils/tableRowNavigation";
import useTableInlineEdit from "../../hooks/InlineEdit/useTableInlineEdit";

const patientPath = ({ patientId, journeyId }) => TabsMap.overview.path(patientId, journeyId);
const renderWithLink = (record, renderFn) => <LinkableCell to={patientPath(record)}>{renderFn()}</LinkableCell>;

const StyledTable = styled(Table)`
  ${(props) => commonTableStyle(props.isFilterListView)}
  .ant-table-thead > tr > th:nth-last-of-type(-n + 2)::before {
    display: none;
  }

  .ant-table {
    max-height: calc(100vh - 262px);
  }

  .ant-table-empty {
    margin: 20px 0px 40px 0px;

    .ant-table-tbody {
      height: calc(100vh - 300px);
    }
  }

  margin-top: 0px;
`;

const RecentPatientsTableComponent = ({
  startSearch,
  fetchOrders,
  tableColumns,
  setTableDataAct,
  tableDataWithRecent,
  isFetching,
  t,
  updateSavedFilterViewParameters = undefined,
  isFilterListView
}) => {
  const [hoveringLineIndex, setHoveringLineIndex] = useState(null);
  const history = useHistory();
  const { renderPatientCell } = useTableInlineEdit({
    t,
    view: selectedViews.recentPatients,
    patientActionCreator: ACTION.updatePatientsInPlace,
    renderWithLink
  });

  useEffect(() => {
    fetchData(1);
  }, []);

  const fetchData = (pagination, filters, sorter) => {
    const targetData = tableDataWithRecent;
    fetchTableData(pagination, sorter, targetData, setTableDataAct, updateSavedFilterViewParameters, true, fetchOrders);
  };

  const {
    patients: recentPatients = [],
    totalPatients,
    selectedPage: recentPatientsCurrentPage
  } = tableDataWithRecent || {};

  const currentPage = recentPatientsCurrentPage;
  const totalRecordsCount = totalPatients;

  const columns = [
    {
      dataIndex: tableColumns[selectedViews.recentPatients]?.star?.dataIndex,
      key: tableColumns[selectedViews.recentPatients]?.star?.key,
      width: 32,
      className: "star-column",
      render: (text, record) => <StarColumn isRecentPatient={true} record={record} />
    },
    {
      ...tableColumns[selectedViews.recentPatients]?.name_id,
      render: (text, record) => renderWithLink(record, () => <PatientNameColumn oneLine={true} record={record} />)
    },
    {
      ...tableColumns[selectedViews.recentPatients]?.assignee,
      width: 230,
      render: (text, record) => renderPatientCell("assignee", record)
    },
    {
      ...tableColumns[selectedViews.recentPatients]?.dob,
      title: tableColumns[selectedViews.recentPatients]?.dob.title,
      render: (text, record) => renderPatientCell("dob", record)
    },
    {
      ...tableColumns[selectedViews.recentPatients]?.viewedAt,
      render: (text, record) => renderWithLink(record, () => <ViewedAtColumn record={record} />)
    },
    {
      dataIndex: tableColumns[selectedViews.recentPatients]?.edit?.dataIndex,
      key: tableColumns[selectedViews.recentPatients]?.edit?.key,
      width: 35,
      render: (_, record, rowIndex) => <EditColumn isHovering={hoveringLineIndex === rowIndex} record={record} />
    }
  ];

  return (
    <>
      <StyledTable
        size="small"
        rowKey="patientId"
        rowClassName="cursor-pointer"
        style={{ width: "100%" }}
        isFilterListView={isFilterListView}
        loading={{ indicator: <TailormedSpinner />, spinning: isFetching || !startSearch }}
        columns={columns.filter((col) => tableColumns[selectedViews.recentPatients]?.[col.key]?.display)}
        locale={{
          emptyText: <EmptyTableMessage />
        }}
        onRow={(record, rowIndex) => {
          return {
            onMouseEnter: () => setHoveringLineIndex(rowIndex),
            onMouseLeave: () => setHoveringLineIndex(null),
            onClick: (event) => handleRowNavigation(event, patientPath(record), history)
          };
        }}
        pagination={{
          size: "small",
          showSizeChanger: false,
          current: currentPage,
          defaultPageSize: PAGE_SIZE,
          pageSize: PAGE_SIZE,
          position: ["bottom"],
          total: totalRecordsCount,
          showTotal: (total, range) => <PatientPageCount total={total} range={range} isAccurateCount={true} />
        }}
        onChange={fetchData}
        dataSource={
          recentPatients.length && startSearch
            ? recentPatients
                .map((patient, index) => ({ ...patient, key: index }))
                .sort((a, b) => b.isActive - a.isActive)
            : null
        }
      />
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  tableColumns: selectTableColumns,
  tableDataWithRecent: selectRecentPatients,
  isFetching: selectIsFetching([ApiNames.getRecentPatients])
});

const mapDispatchToProps = (dispatch) => ({
  setTableDataAct: (page, sorter) => dispatch(ACTION.paginateSortChange(page, selectedViews.recentPatients, sorter))
});

const RecentPatientsTable = compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(RecentPatientsTableComponent);

export { RecentPatientsTable };
